import { Link } from "react-router-dom";
import LegalLayout from "@/components/legal/LegalLayout";

const sections = [
  {
    id: "commitment",
    heading: "Our Commitment",
    body: (
      <>
        <p>
          PaisaCX is registered as a Reporting Entity with the Financial Intelligence Unit – India
          (<strong>FIU-IND</strong>) under the Prevention of Money Laundering Act, 2002
          (&ldquo;<strong>PMLA</strong>&rdquo;) and the rules made under it. We are committed to
          preventing the use of our platform for money laundering, terrorist financing, fraud,
          sanctions evasion or any other financial crime.
        </p>
        <p>
          This policy summarises the controls we apply. It forms part of our{" "}
          <Link to="/terms">Terms of Service</Link>, and personal data collected under it is handled in
          line with our <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </>
    ),
  },
  {
    id: "kyc",
    heading: "Customer Identification (KYC)",
    body: (
      <>
        <p>Every user must complete KYC before trading, depositing INR or withdrawing. We collect and verify:</p>
        <ul>
          <li>full legal name, date of birth and nationality;</li>
          <li>PAN, verified against the Income-tax Department records;</li>
          <li>a proof of address such as Aadhaar (masked), passport, voter ID or driving licence;</li>
          <li>a live selfie or video check to confirm the person matches the documents;</li>
          <li>a verified mobile number and email address;</li>
          <li>bank account or UPI details in the user&rsquo;s own name for INR transfers.</li>
        </ul>
        <p>
          Business accounts additionally provide incorporation documents, details of directors and
          authorised signatories, and identification of beneficial owners.
        </p>
      </>
    ),
  },
  {
    id: "due-diligence",
    heading: "Risk-Based Due Diligence",
    body: (
      <>
        <p>
          We assign each customer a risk rating based on factors such as profile, location, expected
          activity, source of funds and transaction behaviour.
        </p>
        <ul>
          <li><strong>Standard due diligence</strong> applies to most retail users.</li>
          <li><strong>Enhanced due diligence</strong> applies to higher-risk customers, including
              politically exposed persons (PEPs), large-value OTC clients and accounts with unusual
              activity. We may request source-of-funds or source-of-wealth evidence and apply lower
              limits until it is provided.</li>
          <li>KYC information is periodically refreshed, and you must update us promptly when your
              details change.</li>
        </ul>
      </>
    ),
  },
  {
    id: "screening",
    heading: "Sanctions & PEP Screening",
    body: (
      <p>
        Customers are screened at onboarding and on an ongoing basis against applicable sanctions
        lists, including lists notified under the Unlawful Activities (Prevention) Act, 1967 and
        United Nations Security Council lists, as well as PEP and adverse-media sources. We do not
        provide services to sanctioned persons or to residents of sanctioned jurisdictions.
      </p>
    ),
  },
  {
    id: "monitoring",
    heading: "Transaction Monitoring",
    body: (
      <>
        <p>
          We monitor deposits, withdrawals, P2P orders and OTC trades for patterns that may indicate
          financial crime, such as:
        </p>
        <ul>
          <li>rapid movement of funds in and out with little trading activity;</li>
          <li>structuring of transactions to stay below thresholds;</li>
          <li>third-party payments, or payments from accounts in a different name;</li>
          <li>activity inconsistent with the customer&rsquo;s declared profile;</li>
          <li>interaction with wallet addresses linked to illicit activity, using blockchain analytics.</li>
        </ul>
        <p>
          Crypto withdrawals may be limited to whitelisted addresses, and high-risk transactions are
          reviewed by our compliance team before they are processed.
        </p>
      </>
    ),
  },
  {
    id: "p2p-controls",
    heading: "P2P-Specific Controls",
    body: (
      <ul>
        <li>both parties to a P2P trade must be fully KYC-verified;</li>
        <li>fiat payments must be made from and to bank or UPI accounts in the verified user&rsquo;s own name;</li>
        <li>all trade communication must stay in the PaisaCX order chat so it can be reviewed in disputes;</li>
        <li>users who repeatedly receive flagged or reversed payments may be restricted or removed.</li>
      </ul>
    ),
  },
  {
    id: "reporting",
    heading: "Reporting to Authorities",
    body: (
      <p>
        Where required by law, we file Suspicious Transaction Reports and other prescribed reports
        with FIU-IND and respond to lawful requests from law-enforcement agencies, courts and tax
        authorities. As required by law, we do not inform customers when a report has been filed
        about them.
      </p>
    ),
  },
  {
    id: "actions",
    heading: "Account Restrictions",
    body: (
      <p>
        If you fail to provide requested information, provide false information, or if we suspect
        financial crime, we may decline to onboard you, limit features, freeze balances, hold or
        reject transactions, or close your account, and may be legally prohibited from explaining
        the reason.
      </p>
    ),
  },
  {
    id: "records",
    heading: "Record Keeping",
    body: (
      <p>
        We keep KYC records and transaction records for at least five (5) years after the
        relationship ends or the transaction takes place, or longer where required by law or an
        ongoing investigation.
      </p>
    ),
  },
  {
    id: "governance",
    heading: "Governance & Training",
    body: (
      <p>
        PaisaCX has appointed a Designated Director and a Principal Officer responsible for PMLA
        compliance. Our employees receive regular AML training, and this policy and our controls are
        reviewed periodically and independently audited.
      </p>
    ),
  },
  {
    id: "contact",
    heading: "Contact",
    body: (
      <p>
        Questions about this policy, or reports of suspected misuse of the platform, can be sent to{" "}
        <strong>support@paisacx.io</strong> or through our <Link to="/contact">contact form</Link>.
      </p>
    ),
  },
];

export default function AmlPolicy() {
  const intro = (
    <>
      Like every regulated exchange in India, we must know who our customers are. You will need to
      verify your identity (PAN, address proof and a selfie) before trading. We screen users against
      sanctions lists, monitor transactions for suspicious activity, and report to FIU-IND where the
      law requires. Always pay and get paid from accounts in <strong>your own name</strong>.
    </>
  );
  return (
    <LegalLayout
      testid="aml-policy-page"
      title="AML & KYC Policy"
      description="How PaisaCX verifies customers, monitors activity and works with authorities to keep the platform free of financial crime."
      lastUpdated="6 October 2026"
      intro={intro}
      sections={sections}
    />
  );
}
